import Vue from 'vue';

import sleep from '@/utils/sleep';
import Config from '@/config';
import { IS_DEVELOPMENT } from '@/utils/constants';

export const MERGE_DATA = 'MERGE_DATA';
export const PUSH_STEP = 'PUSH_STEP';
export const POP_STEP = 'POP_STEP';
export const SAVE_ANSWER = 'SAVE_ANSWER';
export const SET_REQUESTING_STATE = 'SET_REQUESTING_STATE';
export const ADD_NOTIFICATION = 'ADD_NOTIFICATION';
export const REMOVE_NOTIFICATION = 'REMOVE_NOTIFICATION';
export const SET_WRITING_STATE = 'SET_WRITING_STATE';

const MAX_WRITING_MS = Number(process.env.VUE_APP_MAX_WRITING_TIME || 1500);
const MIN_WRITING_MS = Number(process.env.VUE_APP_MIN_WRITING_TIME || 800);

const steps = Config.get('STEPS')();

const FlowerFactory = ({ steps, initialStep }) => {
  const INITIAL_STEP = initialStep || 'WELCOME_STEP';

  const getStepByName = name => typeof name === 'string' ? ({
    name,
    ...steps[name],
  }) : name;

  const getNextStep = (nextStep, store) =>
    getStepByName(typeof nextStep === 'string' || nextStep === false ? nextStep : nextStep({ store }));

  const afterSend = async (store, currentStep) => {
    let nextStep = currentStep?.nextStep;

    /**
     * Si el step tiene un hook beforeNext lo llamamos
     */
    if(currentStep.beforeNext) {
      store.commit(SET_REQUESTING_STATE, true);

      const beforeNextResponse = await currentStep.beforeNext({ store });

      store.commit(SET_REQUESTING_STATE, false);


      /**
       * El hook puede devolver varios tipos de valores
       *
       * string: Indicando el nombre del proximo step
       * function: Para parametrizar la seleccion del proximo step
       * booleano: para indicar un false y no tomar en cuenta el siguiente step
       */
      if(['string', 'function'].includes(typeof beforeNextResponse) || beforeNextResponse == false) nextStep = beforeNextResponse;
    }

    nextStep = getNextStep(nextStep, store);

    if(nextStep) store.dispatch('goToStep', { step: nextStep });
  };

  const initState = {

    data: {
      product: null,
    },
    adviserIsWriting: false,
    isRequesting: false,
    answers: {},
    lastAnswer: null,
    steps: [],
    notification: null,
  };

  const mutations = {
    [MERGE_DATA](state, data) {
      Object.assign(state.data, data);
    },

    [SAVE_ANSWER](state, { step, answer }) {
      Vue.set(state.answers, step, answer);
      Vue.set(state, 'lastAnswer', answer)
    },

    [PUSH_STEP](state, { step }) {
      state.steps.push(step);
    },

    [SET_REQUESTING_STATE](state, val) {
      state.isRequesting = val;
    },

    [POP_STEP](state, amount = 1) {
      state.steps.splice(state.steps.length - amount, amount);
    },

    [ADD_NOTIFICATION](state, { type, body }) {
      state.notification = { type, body };
    },

    [REMOVE_NOTIFICATION](state) {
      state.notification = null;
    },

    [SET_WRITING_STATE](state, val) {
      state.adviserIsWriting = val
    },
  };

  const actions = {
    async init({ commit, dispatch }, { product, motive }) {
      commit(MERGE_DATA, { product, motive });

      dispatch('goToStep', { step: getStepByName(INITIAL_STEP) });
    },

    async nextStep(store) {
      const currentStep = store.getters.currentStep;

      if(currentStep?.nextStep) store.dispatch('goToStep', { step: getNextStep(currentStep?.nextStep, store) });
    },

    async goToStep(store, { step }) {
      const { commit } = store;

      commit(SET_WRITING_STATE, true);

      await sleep(Math.floor(Math.random() * (MAX_WRITING_MS - MIN_WRITING_MS + 1)) + MIN_WRITING_MS);

      commit(SET_WRITING_STATE, false);

      commit(REMOVE_NOTIFICATION);
      commit(PUSH_STEP, {
        step: {
          ...step,
          question: typeof step.question === 'function' ? step.question({ store }) : step.question,
          choice: typeof step.choice === 'function' ? step.choice({ store }) : step.choice,
        },
      });

      // Lifecycle callback
      if(step.mounted) await step.mounted({ store });
    },

    async sendChoice(store, choice) {
      const { commit, getters: { currentStep }}= store;

      commit(SAVE_ANSWER, { step: currentStep.name, answer: choice });

      if(choice.href) window.location.href = choice.href
      else afterSend(store, currentStep);
    },

    async sendForm(store , formData) {
      const { commit, getters: { currentStep } } = store;

      commit(SAVE_ANSWER, { step: currentStep.name, answer: formData });
      commit(MERGE_DATA, {
        [currentStep.form.dataKey]: {
          ...store.state.data?.[currentStep.form.dataKey],
          ...formData,
        },
      });

      afterSend(store, currentStep);
    },

    async goToPreviousStep({ commit, getters: { lastRewindableStep, steps } }) {
      const previousStepIndex = steps.indexOf(lastRewindableStep);

      commit(POP_STEP, (steps.length - 1) - previousStepIndex );
    },

    addNotification({ commit }, notification) {
      commit(ADD_NOTIFICATION, notification);
    },

    removeNotification({ commit }) {
      commit(REMOVE_NOTIFICATION);
    },
  };

  const getters = {
    steps: state => state.steps,
    currentStep: state => state.steps[state.steps.length - 1],
    progress: (_, { currentStep }) => currentStep?.progress,
    hasNextStep: (_, { currentStep }) => ['string', 'function'].includes(typeof currentStep?.nextStep),
    product: state => state.data?.basic?.product,
    lastRewindableStep: ({ steps }) => steps
      .slice(0, steps.length - 1)
      .reverse()
      .find(({ choice, form}) => choice || form),
    canRewind: (_, { lastRewindableStep }) => lastRewindableStep ? true : false,
    lastAnswer: state => state.lastAnswer,
    stepAnswer: state => stepName => state.answers[stepName],
    isRequesting: state => state.isRequesting,
    populatedData: state => state.data,
    nextButtonText: (_, { currentStep }) => currentStep?.nextButtonText,
    notification: state => state.notification,
    adviserIsWriting: state => state.adviserIsWriting,
  };

  return {
    namespaced: true,

    state: { ...initState },

    getters,

    mutations,

    actions,
  };
};

export default FlowerFactory({
  initialStep: IS_DEVELOPMENT ? 'WELCOME_STEP' : 'WELCOME_STEP',
  steps,
});
